import {
  Box,
  FormControl,
  FormHelperText,
  Typography,
  MenuItem,
  ListItemText,
} from '@mui/material';
import { FC } from 'react';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { RolesFieldProps } from 'src/models/FieldProps.interface';
import { rolePriorities } from 'src/utils/properties.utils';
import {
  StyledRoleSelect,
  StyledRoleCheckbox,
  roleSelectMenuProps,
  roleLabelStyles,
  roleListItemTextStyles,
  roleErrorIconStyles,
} from 'src/components/StyledComponents/StyledRoleDropdown';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { unitErrorTextStyles } from '../StyledComponents/StyledUnitPicker';

const RolesField: FC<RolesFieldProps> = ({
  id,
  label,
  formikProps,
  options,
  sx,
  errorState = false,
  userRole,
  isEditingOwnAccount,
  height = '40px',
  width = '450px',
}) => {
  const hasError =
    formikProps.touched[id] && (Boolean(formikProps.errors[id]) || errorState);

  const currentValue = Array.isArray(formikProps.values[id])
    ? formikProps.values[id]
    : [];

  return (
    <Box mb={3}>
      <Typography sx={roleLabelStyles}>{label}</Typography>
      <FormControl fullWidth error={hasError} sx={{ m: 0 }}>
        <StyledRoleSelect
          id={id}
          name={id}
          multiple
          value={currentValue}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
          displayEmpty
          height={height}
          width={width}
          hasError={hasError}
          sx={sx}
          IconComponent={ExpandMoreOutlinedIcon}
          renderValue={(selected) => {
            const selectedRoles = Array.isArray(selected) ? selected : [];
            selectedRoles.sort(
              (a, b) =>
                (rolePriorities[a.toLowerCase()] ?? 999) -
                (rolePriorities[b.toLowerCase()] ?? 999)
            );
            return selectedRoles.length > 0
              ? selectedRoles.map(capitalizeFirstLetter).join(', ')
              : `Select ${label}`;
          }}
          MenuProps={roleSelectMenuProps}
        >
          {options.map((option) => {
            const isSelected = currentValue.includes(option.value);
            const isDisabled =
              (option.value === 'owner' && userRole !== 'owner') ||
              (option.value === 'admin' &&
                isEditingOwnAccount &&
                userRole !== 'owner') ||
              (option.value === 'owner' && isEditingOwnAccount);

            return (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={isDisabled}
              >
                <StyledRoleCheckbox checked={isSelected} />
                <ListItemText
                  primary={option.label}
                  sx={roleListItemTextStyles(isSelected)}
                />
              </MenuItem>
            );
          })}
        </StyledRoleSelect>
        {formikProps.touched[id] && formikProps.errors[id] && (
          <FormHelperText sx={{ ...unitErrorTextStyles }}>
            <Box
              component="i"
              className="bx bxs-error-circle"
              sx={roleErrorIconStyles}
            />
            {formikProps.errors[id]}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default RolesField;
